import type { AuthUser, Permission, SetupAdminRequest } from '@bale-bot/types';
import { create } from 'zustand';
import { api } from '../services/api';

type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  /** True while the seeded default admin must still be replaced. */
  mustCreateAdmin: boolean;
  /** Asks the API who we are; the session lives in an HttpOnly cookie. */
  bootstrap: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  setupAdmin: (payload: SetupAdminRequest) => Promise<void>;
  logout: () => Promise<void>;
  clear: () => void;
}

/**
 * Admin session state.
 *
 * Nothing secret is held here: the session is an HttpOnly cookie the browser
 * attaches on its own, so a refresh means asking `/auth/me` again rather than
 * reading a token out of storage that any XSS could steal too.
 */
export const useAuthStore = create<AuthState>((set) => ({
  status: 'unknown',
  user: null,
  mustCreateAdmin: false,

  async bootstrap() {
    try {
      const session = await api.me();
      set({
        status: 'authenticated',
        user: session.user,
        mustCreateAdmin: session.mustCreateAdmin,
      });
    } catch {
      // 401 is the normal "not signed in yet" answer.
      set({ status: 'anonymous', user: null, mustCreateAdmin: false });
    }
  },

  async login(username, password) {
    const session = await api.login({ username, password });
    set({
      status: 'authenticated',
      user: session.user,
      mustCreateAdmin: session.mustCreateAdmin,
    });
  },

  async setupAdmin(payload) {
    const session = await api.setupAdmin(payload);
    set({
      status: 'authenticated',
      user: session.user,
      mustCreateAdmin: session.mustCreateAdmin,
    });
  },

  async logout() {
    try {
      await api.logout();
    } finally {
      set({ status: 'anonymous', user: null, mustCreateAdmin: false });
    }
  },

  clear() {
    set({ status: 'anonymous', user: null, mustCreateAdmin: false });
  },
}));

/** Client-side convenience only — the API enforces the same rules. */
export function hasPermission(user: AuthUser | null, permission: Permission): boolean {
  return user?.permissions.includes(permission) ?? false;
}